import clsx from "clsx";
import React, { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  HashtagString,
  createHashStateHook,
} from "../../../../stores/useHashState";
import { PrimaryButton } from "../../../../ui/Button/Button";
import { Dialog } from "../../../../ui/Dialog/Dialog";
import { DialogHeader } from "../../../../ui/Dialog/DialogHeader";
import { useOrderDetails } from "../../useOrderDetails";
import { ContactDealerForm } from "../ContactDealerForm";
import { XTimeAppointmentFrame } from "../XTimeAppointment/XTimeAppointmentModal";
import { useXtimeAppointmentState } from "../XTimeAppointment/useXtimeAppointmentState";
import {
  clearPendingQuoteEmail,
  useSendPendingQuoteEmail,
} from "./pendingQuoteEmail";

// Opened once "Next Step" has saved the quote (TW-1134): the dealer's X-Time booking when the integration is on,
// otherwise the built-in appointment request form.
export const appointmentStepHash: HashtagString = "#appointment-step";

export const useAppointmentStepState = createHashStateHook(appointmentStepHash);

export const AppointmentStepModal: FunctionComponent = () => {
  const [visible, { hide }] = useAppointmentStepState();
  const { t } = useTranslation();
  const isXtime = useXtimeAppointmentState();
  const { data } = useOrderDetails();
  const sendPendingQuoteEmail = useSendPendingQuoteEmail();
  // Once the appointment request is sent, OK closes the step and Skip is gone (it would read as "skip the booking").
  const [requestSent, setRequestSent] = useState(false);

  // Skip, OK, X, Escape and clicking outside all end the step: the quote email goes out now, unless an appointment
  // request already emailed the quote.
  const close = (): void => {
    sendPendingQuoteEmail();
    setRequestSent(false);
    hide();
  };

  return (
    <Dialog
      className={clsx(
        "w-full lg:max-w-[48rem] flex flex-col",
        isXtime && "h-full"
      )}
      id="appointment-step-modal"
      onClose={close}
      placement="top"
      visible={visible}
    >
      <DialogHeader
        className="mb-4"
        onClose={close}
        subtitle={t("cart.appointmentStepSubtitle")}
        title={t("cart.appointmentStepTitle")}
      />
      {/* Mounted only while open so the forms pick up the details just saved with the quote. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {visible &&
          (isXtime ? (
            <XTimeAppointmentFrame />
          ) : (
            <ContactDealerForm
              onRequestSent={(): void => {
                clearPendingQuoteEmail();
                setRequestSent(true);
              }}
              onDone={close}
              orderDetails={data}
            />
          ))}
      </div>
      {!requestSent && (
        <footer className="pt-4 flex justify-end">
          <PrimaryButton
            aria-label="skipAppointmentStep"
            className="h-14 px-10 text-lg"
            onClick={close}
            type="button"
          >
            {t("cart.appointmentStepSkip")}
          </PrimaryButton>
        </footer>
      )}
    </Dialog>
  );
};
